'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { baseDashboardClasses } from '../BaseDashboard'
import { apiDelete, apiGet, apiPost, apiPut } from './entriesApi'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'

// The words a brand is sourced with. Until this existed they lived in
// BRAND_CONFIGS in domain_sourcing.py, so widening a brand — adding Brisa
// alongside Via Verde, adding a lure word a campaign started using — meant a
// code change and a deploy, while the strategies those words feed have been
// editable rows since day one.
//
// The preview is the point of the page, not a nicety. Each anchor multiplies
// into lure combinations and each keyword is another MerkleMap search, and a
// browser-mode sweep costs ~3 minutes per search on one laptop. A careless
// '*verde*' is a wasted night and a dashboard full of stevia, and the only
// way to see that coming is to count the queries before saving.

type BrandRow = {
  platform_id: number
  brand_name: string | null
  brand_desc: string | null
  brand_keywords: string[]
  brand_anchors: string[]
  root_domains: string[]
  legit_suffixes: string[]
  lure_words: string[]
  cheap_tlds: string[] | null
  notes: string | null
  is_active: boolean
  updated_at?: string
  updated_by?: string | null
}

type PreviewResult = {
  platform_id: number
  brand_source: string
  seeds: number
  total_queries: number
  by_strategy: Record<string, number>
  start_kind: string
  queries: Array<{ strategy: string; start_kind: string; query_string: string; query_type: string; source_score: number }>
}

type JobRunRow = { id: string; status: string; result?: PreviewResult | null; error?: string | null; progress?: any }

// Per suggested term, the reported hostnames that contain it and how many scam
// messages mention it -- checked by the worker against the evidence, not the
// model's say-so. Keywords, anchors and lure words only; the brand's own
// domains are nothing a campaign used.
type TermSource = { hosts: string[]; host_count: number; message_count: number }
type Provenance = Record<string, Record<string, TermSource>>
type Suggestion = {
  notes: string
  evidence: { seeds: number; messages: number; has_config: boolean }
  provenance?: Provenance
  hostnames?: string[]
}

const FIELDS: Array<{ key: keyof BrandRow; label: string; hint: string }> = [
  { key: 'brand_keywords', label: 'Keywords', hint: "Searched on their own ('*viaverde*'). Keep distinctive — an ordinary word returns the dictionary." },
  { key: 'brand_anchors', label: 'Anchors', hint: "Searched combined with a lure ('*brisa*portagem*'), and — when the anchor is also a keyword — anchored to a TLD ('ctt*.com', '*ctt.com'). A common word is safe here only while it is not a keyword." },
  { key: 'root_domains', label: 'Root domains', hint: "The brand's real domains. Give the highest-precision query there is: '*viaverde.pt.*'." },
  { key: 'legit_suffixes', label: 'Legit suffixes', hint: 'Anything ending in these is the brand’s own host and never a candidate.' },
  { key: 'lure_words', label: 'Lure words', hint: 'The narrative: unpaid tolls, parcel redelivery, deposit refunds.' },
  { key: 'cheap_tlds', label: 'Cheap TLDs (optional)', hint: 'Leave empty to use the shared list. Only override when a brand needs its own.' },
]

const EMPTY: BrandRow = {
  platform_id: 0, brand_name: null, brand_desc: null,
  brand_keywords: [], brand_anchors: [], root_domains: [], legit_suffixes: [], lure_words: [],
  cheap_tlds: null, notes: null, is_active: true,
}

const toText = (list: string[] | null | undefined) => (list ?? []).join('\n')
const toList = (text: string) => text.split(/[\n,]/).map((s) => s.trim().toLowerCase()).filter(Boolean)

export function BrandVocabularyEditor({
  theme, platformId, platformName,
}: {
  theme: ProcessedTheme
  platformId: number
  platformName?: string | null
}) {
  const { isDark, border, panelBg, softBg, label, muted, text } = baseDashboardClasses(theme)

  const [saved, setSaved] = useState<BrandRow | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [suggesting, setSuggesting] = useState(false)
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [previewNote, setPreviewNote] = useState<string | null>(null)

  const fieldsToDraft = useCallback((row: BrandRow | null) => {
    const base = row ?? EMPTY
    const next: Record<string, string> = {}
    for (const f of FIELDS) next[f.key as string] = toText(base[f.key] as string[] | null)
    next.brand_name = base.brand_name ?? ''
    next.brand_desc = base.brand_desc ?? ''
    next.notes = base.notes ?? ''
    return next
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const result = await apiGet<BrandRow | null>(`/auto-domains/brands/${platformId}`)
    setLoading(false)
    if (!result.success) {
      setError(typeof result.error === 'string' ? result.error : 'Could not load this brand')
      return
    }
    setSaved(result.data ?? null)
    setDraft(fieldsToDraft(result.data ?? null))
  }, [platformId, fieldsToDraft])

  useEffect(() => { void load() }, [load])

  const draftPayload = useMemo(() => {
    const payload: Record<string, any> = {}
    for (const f of FIELDS) payload[f.key as string] = toList(draft[f.key as string] ?? '')
    if (payload.cheap_tlds.length === 0) payload.cheap_tlds = null
    return payload
  }, [draft])

  const dirty = useMemo(() => {
    const base = saved ?? EMPTY
    for (const f of FIELDS) {
      const a = (draftPayload[f.key as string] ?? []) as string[]
      const b = (base[f.key] as string[] | null) ?? []
      if (a.join('|') !== b.join('|')) return true
    }
    return (draft.brand_name ?? '') !== (base.brand_name ?? '')
      || (draft.brand_desc ?? '') !== (base.brand_desc ?? '')
      || (draft.notes ?? '') !== (base.notes ?? '')
  }, [draftPayload, draft, saved])

  // The plan is built by the Python planner, so a preview is a job run: queue
  // one and poll it. It searches nothing and writes nothing.
  const runPreview = async () => {
    setPreviewing(true)
    setPreviewNote('queued…')
    setPreview(null)
    const queued = await apiPost<{ id: string }>('/auto-domains/brands/preview', {
      platform_id: platformId,
      draft: draftPayload,
    })
    if (!queued.success || !queued.data?.id) {
      setPreviewing(false)
      setPreviewNote(typeof queued.error === 'string' ? queued.error : 'Could not queue the preview')
      return
    }
    const runId = queued.data.id
    for (let attempt = 0; attempt < 40; attempt++) {
      await new Promise((r) => setTimeout(r, 1500))
      const run = await apiGet<JobRunRow>(`/auto-domains/brands/preview/${runId}`)
      const row = run.data
      if (!run.success || !row) continue
      if (row.status === 'succeeded') {
        setPreview((row.result as PreviewResult) ?? null)
        setPreviewNote(null)
        setPreviewing(false)
        return
      }
      if (row.status === 'failed' || row.status === 'cancelled') {
        setPreviewNote(row.error ? `preview ${row.status}: ${row.error.slice(0, 200)}` : `preview ${row.status}`)
        setPreviewing(false)
        return
      }
      setPreviewNote(row.status === 'queued' ? 'waiting for a worker…' : 'planning…')
    }
    setPreviewing(false)
    // A preview that never starts almost always means nothing is listening,
    // which is worth saying plainly rather than spinning forever.
    setPreviewNote('no worker answered — is an auto-domains worker online? (Administration › Jobs)')
  }

  // "Suggest with AI": a job run like the preview. The worker gathers what
  // the system knows about the platform -- reported hostnames, real scam
  // messages -- and asks the analysis model for a draft. It lands in the
  // boxes as an unsaved draft: Preview it, edit it, then Save.
  const suggest = async () => {
    setSuggesting(true)
    setPreviewNote('asking the model…')
    setSuggestion(null)
    const queued = await apiPost<{ id: string }>('/auto-domains/brands/suggest', { platform_id: platformId })
    if (!queued.success || !queued.data?.id) {
      setSuggesting(false)
      setPreviewNote(typeof queued.error === 'string' ? queued.error : 'Could not queue the suggestion')
      return
    }
    for (let attempt = 0; attempt < 60; attempt++) {
      await new Promise((r) => setTimeout(r, 2000))
      const run = await apiGet<JobRunRow & { result?: any }>(`/auto-domains/brands/preview/${queued.data.id}`)
      const row = run.data
      if (!run.success || !row) continue
      if (row.status === 'succeeded' && row.result?.draft) {
        const d = row.result.draft as Record<string, string[]>
        setDraft((prev) => ({
          ...prev,
          brand_keywords: toText(d.brand_keywords), brand_anchors: toText(d.brand_anchors),
          root_domains: toText(d.root_domains), legit_suffixes: toText(d.legit_suffixes),
          lure_words: toText(d.lure_words),
        }))
        setSuggestion({
          notes: row.result.notes ?? '', evidence: row.result.evidence,
          provenance: row.result.provenance, hostnames: row.result.hostnames,
        })
        setPreviewNote(null)
        setSuggesting(false)
        return
      }
      if (row.status === 'failed' || row.status === 'cancelled') {
        setPreviewNote(row.error ? `suggestion ${row.status}: ${row.error.slice(0, 200)}` : `suggestion ${row.status}`)
        setSuggesting(false)
        return
      }
      setPreviewNote(row.status === 'queued' ? 'waiting for a worker…' : 'asking the model…')
    }
    setSuggesting(false)
    setPreviewNote('no worker answered — is an auto-domains worker online? (Administration › Jobs)')
  }

  const save = async () => {
    setSaving(true)
    setError(null)
    setMessage(null)
    const result = await apiPut<BrandRow>(`/auto-domains/brands/${platformId}`, {
      ...draftPayload,
      brand_name: draft.brand_name || null,
      brand_desc: draft.brand_desc || null,
      notes: draft.notes || null,
    })
    setSaving(false)
    if (!result.success) {
      setError(typeof result.error === 'string' ? result.error : 'Could not save')
      return
    }
    setSaved(result.data ?? null)
    setDraft(fieldsToDraft(result.data ?? null))
    setMessage('Saved. The next sourcing run for this platform uses these words.')
  }

  const revert = async () => {
    setSaving(true)
    const result = await apiDelete(`/auto-domains/brands/${platformId}`)
    setSaving(false)
    if (!result.success) {
      setError(typeof result.error === 'string' ? result.error : 'Could not revert')
      return
    }
    setMessage('Reverted to the built-in configuration.')
    await load()
  }

  const inputCls = `w-full rounded-sm border px-2 py-1.5 text-[12px] font-mono ${border} ${softBg} ${text} outline-none focus:border-signal`

  return (
    <div className={`rounded-sm border ${border} ${panelBg} p-4`}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <div className={`text-[13px] font-semibold ${text}`}>
            Sourcing vocabulary{platformName ? ` · ${platformName}` : ''}
          </div>
          <div className={`text-[11px] ${muted}`}>
            {loading ? 'Loading…'
              : saved
                ? `Saved${saved.updated_at ? ` ${new Date(saved.updated_at).toLocaleString()}` : ''}${saved.updated_by ? ` by ${saved.updated_by}` : ''}`
                : 'No saved row — this platform uses the built-in configuration.'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void suggest()}
            disabled={suggesting || previewing || loading}
            title="Draft the vocabulary from this brand's reported hostnames and scam messages"
            className={`rounded-sm border px-3 py-1.5 text-[11px] font-medium ${border} text-violet-700 hover:bg-secondary disabled:opacity-50`}
          >
            {suggesting ? 'Suggesting…' : '✦ Suggest with AI'}
          </button>
          <button
            type="button"
            onClick={() => void runPreview()}
            disabled={previewing || loading}
            className={`rounded-sm border px-3 py-1.5 text-[11px] font-medium ${border} text-signal hover:bg-secondary disabled:opacity-50`}
          >
            {previewing ? 'Previewing…' : 'Preview plan'}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || loading || !dirty}
            className="rounded-sm bg-primary text-primary-foreground px-3 py-1.5 text-[11px] font-medium text-white hover:bg-signal disabled:opacity-40"
          >
            {saving ? 'Saving…' : dirty ? 'Save' : 'Saved'}
          </button>
          {saved && (
            <button
              type="button"
              onClick={() => void revert()}
              disabled={saving}
              className={`rounded-sm border px-3 py-1.5 text-[11px] ${border} ${muted} hover:text-destructive disabled:opacity-50`}
            >
              Revert to built-in
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-2 rounded-sm bg-danger-surface px-2 py-1 text-[11px] text-destructive">{error}</div>}
      {suggestion && (
        <div className={`mb-3 rounded-sm border px-3 py-2 text-[11px] border-violet-300 bg-violet-50 text-violet-900`}>
          <div className="font-medium">✦ Suggested by the analysis model — unsaved. Review, Preview plan, then Save.</div>
          <div className={`mt-0.5 ${muted}`}>
            From {suggestion.evidence.messages} scam message{suggestion.evidence.messages === 1 ? '' : 's'} and {suggestion.evidence.seeds} reported hostname{suggestion.evidence.seeds === 1 ? '' : 's'}
            {suggestion.evidence.has_config ? ', on top of the existing vocabulary' : ''}.
          </div>
          {suggestion.notes && <div className="mt-1">{suggestion.notes}</div>}
          {!!suggestion.hostnames?.length && (
            <details className="mt-1">
              <summary className="cursor-pointer">Hostnames the model was given ({suggestion.hostnames.length})</summary>
              <div className={`mt-1 font-mono ${muted}`}>{suggestion.hostnames.join(' · ')}</div>
            </details>
          )}
        </div>
      )}
      {message && <div className="mb-2 rounded-sm bg-success-surface px-2 py-1 text-[11px] text-success">{message}</div>}

      <div className="grid gap-3 md:grid-cols-2">
        {FIELDS.map((f) => {
          const count = (draftPayload[f.key as string] as string[] | null)?.length ?? 0
          return (
            <div key={f.key as string}>
              <div className="flex items-baseline justify-between">
                <label className={`text-[11px] font-medium uppercase tracking-wider ${label}`}>{f.label}</label>
                <span className={`text-[10px] font-mono ${muted}`}>{count}</span>
              </div>
              <textarea
                rows={5}
                value={draft[f.key as string] ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, [f.key as string]: e.target.value }))}
                placeholder="one per line"
                className={inputCls}
              />
              {suggestion?.provenance?.[f.key as string] && (
                <TermSources
                  terms={(draftPayload[f.key as string] as string[] | null) ?? []}
                  sources={suggestion.provenance[f.key as string]}
                  isDark={isDark}
                  muted={muted}
                />
              )}
              <div className={`mt-1 text-[10px] ${muted}`}>{f.hint}</div>
            </div>
          )
        })}
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <div>
          <label className={`text-[11px] font-medium uppercase tracking-wider ${label}`}>Brand name</label>
          <input value={draft.brand_name ?? ''} onChange={(e) => setDraft((p) => ({ ...p, brand_name: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className={`text-[11px] font-medium uppercase tracking-wider ${label}`}>Notes</label>
          <input value={draft.notes ?? ''} onChange={(e) => setDraft((p) => ({ ...p, notes: e.target.value }))} className={inputCls} />
        </div>
      </div>

      {(preview || previewNote) && (
        <div className={`mt-4 rounded-sm border ${border} ${softBg} p-3`}>
          {previewNote && <div className={`text-[11px] ${muted}`}>{previewNote}</div>}
          {preview && (
            <>
              <div className={`mb-2 text-[12px] ${text}`}>
                <span className="font-semibold">{preview.total_queries}</span> queries
                <span className={muted}> · {preview.start_kind} start · {preview.seeds} seeds · from {preview.brand_source}</span>
              </div>
              {/* Browser-mode sweeps run about one search every three minutes on
                  the one laptop, so the honest unit for a plan is hours. */}
              <div className={`mb-2 text-[11px] ${muted}`}>
                ≈ {Math.round((preview.total_queries * 2.8) / 6) / 10} h of browser-mode searching
              </div>
              <div className="mb-2 flex flex-wrap gap-1">
                {Object.entries(preview.by_strategy).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                  <span key={name} className={`rounded-sm border px-1.5 py-0.5 text-[10px] font-mono ${border} ${muted}`}>{name} {n}</span>
                ))}
              </div>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-[11px]">
                  <tbody>
                    {preview.queries.map((q, i) => (
                      <tr key={`${q.query_string}-${i}`} className={'border-b border-border'}>
                        <td className={`py-0.5 pr-2 font-mono ${muted}`}>{q.source_score.toFixed(3)}</td>
                        <td className={`py-0.5 pr-2 ${muted}`}>{q.strategy}</td>
                        <td className={`py-0.5 font-mono ${text}`}>{q.query_string}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.total_queries > preview.queries.length && (
                <div className={`mt-1 text-[10px] ${muted}`}>
                  showing the {preview.queries.length} highest-scoring of {preview.total_queries}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// Under a box, after "Suggest with AI": which of its terms the known campaigns
// contain (hover for the hostnames) and which are only the model's general
// knowledge. Follows the box as it is edited; a term typed by hand was never
// suggested and is left unmarked.
function TermSources({
  terms, sources, isDark, muted,
}: {
  terms: string[]
  sources: Record<string, TermSource>
  isDark: boolean
  muted: string
}) {
  const fromCampaigns = terms.filter((t) => sources[t] && (sources[t].host_count > 0 || sources[t].message_count > 0))
  const modelOnly = terms.filter((t) => sources[t] && sources[t].host_count === 0 && sources[t].message_count === 0)
  if (!fromCampaigns.length && !modelOnly.length) return null
  const chip = 'rounded-sm border px-1 py-px font-mono text-[10px]'
  const campaignCls = isDark ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-emerald-300 bg-emerald-50 text-emerald-800'
  const modelCls = isDark ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-amber-300 bg-amber-50 text-amber-800'
  const why = (s: TermSource) => [
    s.host_count ? `in ${s.host_count} reported hostname${s.host_count === 1 ? '' : 's'}: ${s.hosts.join(', ')}${s.host_count > s.hosts.length ? ', …' : ''}` : '',
    s.message_count ? `mentioned in ${s.message_count} scam message${s.message_count === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ')
  return (
    <div className="mt-1 space-y-1 text-[10px]">
      {fromCampaigns.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className={muted}>From known campaigns:</span>
          {fromCampaigns.map((t) => (
            <span key={t} title={why(sources[t])} className={`${chip} ${campaignCls}`}>
              {t} <span className="opacity-70">{sources[t].host_count}h·{sources[t].message_count}m</span>
            </span>
          ))}
        </div>
      )}
      {modelOnly.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className={muted}>Model&apos;s general knowledge, in no campaign — check before saving:</span>
          {modelOnly.map((t) => <span key={t} className={`${chip} ${modelCls}`}>{t}</span>)}
        </div>
      )}
    </div>
  )
}
